import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { debounce } from 'lodash'
import PropTypes from 'prop-types'
import {
  Button,
  Col,
  DatePicker,
  Form,
  message,
  Row,
  Select,
  Table,
  Tag
} from 'antd'
import TransactionForm from './TransactionForm'
import EvidenceImage from './EvidenceImage'
import {
  TRANSACTION_STATUS,
  fetchActivityTransactions,
  fetchAllActivityTransactions,
  fetchFilterBrandActivities,
  fetchFilterBrands,
  searchFilterHeroes,
  searchFilterUsers,
  formatAmount,
  formatBangkokDateTime,
  showApiError,
  updateActivityTransaction
} from '../../../../../modules/brandActivityTransaction'
import { RESPONSE_MESSAGE } from '../../../../../utils/constants'

const { RangePicker } = DatePicker

const todayRange = () => [moment(), moment()]

const MAX_RANGE_MONTHS = 3

const isRangeTooLong = (start, end) =>
  end.isAfter(start.clone().add(MAX_RANGE_MONTHS, 'months'), 'day')

// default แสดงเฉพาะ transaction ของวันนี้
const initialOptions = () => {
  const today = moment().format('YYYY-MM-DD')
  return {
    page: 0,
    size: 20,
    startDate: today,
    endDate: today
  }
}

const statusLabel = (value) => {
  const status = TRANSACTION_STATUS.find((item) => item.value === value)
  return status ? status.label : value || '-'
}

const nameWithId = (name, id) => {
  if (name) return `${name} (#${id})`
  return '-'
}

// ไม่ส่ง brandActivityId = แสดง transaction ของทุก activity
const TransactionTab = ({ brandActivityId, materials, form }) => {
  const isAllActivity = !brandActivityId
  const { getFieldDecorator, validateFields, resetFields, setFieldsValue } =
    form
  const [filterOption, setFilterOption] = useState(initialOptions())
  const [transactions, setTransactions] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [visibleForm, setVisibleForm] = useState(false)
  const [formState, setFormState] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [userOptions, setUserOptions] = useState([])
  const [heroOptions, setHeroOptions] = useState([])
  const [brandOptions, setBrandOptions] = useState([])
  const [activityOptions, setActivityOptions] = useState([])
  const [pickedStartDate, setPickedStartDate] = useState(null)

  // ห้ามเลือกวันในอนาคต และช่วงวันที่ไม่เกิน 3 เดือนจากวันแรกที่เลือก
  function disabledDate(current) {
    if (!current) return false
    if (current.isAfter(moment(), 'day')) return true
    if (!pickedStartDate) return false
    return (
      isRangeTooLong(pickedStartDate, current) ||
      isRangeTooLong(current, pickedStartDate)
    )
  }

  function onCalendarChange(dates) {
    setPickedStartDate(dates && dates.length === 1 ? dates[0] : null)
  }

  function onDateRangeOpenChange(open) {
    if (!open) setPickedStartDate(null)
  }

  const searchUsers = useRef(
    debounce(async (keyword) => {
      try {
        setUserOptions(await searchFilterUsers(keyword))
      } catch (err) {
        setUserOptions([])
      }
    }, 500)
  ).current

  const searchHeroes = useRef(
    debounce(async (keyword) => {
      try {
        setHeroOptions(await searchFilterHeroes(keyword))
      } catch (err) {
        setHeroOptions([])
      }
    }, 500)
  ).current

  useEffect(() => {
    searchUsers('')
    searchHeroes('')
    if (isAllActivity) {
      fetchFilterBrands()
        .then(setBrandOptions)
        .catch(() => setBrandOptions([]))
    }
  }, [])

  // activity ดึงได้ทีละ brand
  async function onBrandChange(brandId) {
    setFieldsValue({ brandActivityId: undefined })
    setActivityOptions([])
    if (!brandId) return
    try {
      setActivityOptions(await fetchFilterBrandActivities(brandId))
    } catch (err) {
      setActivityOptions([])
    }
  }

  async function fetchData(options) {
    setIsLoading(true)
    try {
      const data = isAllActivity
        ? await fetchAllActivityTransactions(options)
        : await fetchActivityTransactions(brandActivityId, options)
      setTransactions(data)
    } catch (err) {
      showApiError(err)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    const options = initialOptions()
    resetFields()
    setFilterOption(options)
    fetchData(options)
  }, [brandActivityId])

  const search = (options) => {
    setFilterOption(options)
    fetchData(options)
  }

  function onSubmit(e) {
    e.preventDefault()
    validateFields((err, values) => {
      if (!err) {
        const dateRange = values.dateRange || []
        search({
          page: 0,
          size: filterOption.size,
          startDate: dateRange[0]
            ? dateRange[0].format('YYYY-MM-DD')
            : undefined,
          endDate: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
          status: values.status,
          userId: values.userId,
          heroId: values.heroId,
          materialId: values.materialId,
          brandActivityId: values.brandActivityId
        })
      }
    })
  }

  function onClear() {
    resetFields()
    setActivityOptions([])
    search({ ...initialOptions(), size: filterOption.size })
  }

  function onChange(page) {
    search({ ...filterOption, page: page - 1 })
  }

  function onShowSizeChange(current, pageSize) {
    search({ ...filterOption, page: 0, size: pageSize })
  }

  const onClickForm = (record) => {
    setFormState(record)
    setVisibleForm(true)
  }

  const onCloseForm = () => {
    setVisibleForm(false)
    setFormState(null)
  }

  async function onOk(body) {
    if (Object.keys(body).length === 0) {
      onCloseForm()
      return
    }
    setIsSaving(true)
    try {
      await updateActivityTransaction(
        formState.brandActivityId || brandActivityId,
        formState.id,
        body
      )
      message.success(RESPONSE_MESSAGE.SUCCESS)
      onCloseForm()
      fetchData(filterOption)
    } catch (err) {
      if (showApiError(err) === 404) {
        onCloseForm()
        fetchData(filterOption)
      }
    }
    setIsSaving(false)
  }

  const columns = [
    {
      title: 'ลำดับ',
      key: 'no',
      width: 70,
      align: 'center',
      render: (text, record, index) => (
        <span>{filterOption.page * filterOption.size + index + 1}</span>
      )
    },
    {
      title: 'วันเวลา',
      dataIndex: 'createdDate',
      key: 'createdDate',
      render: (createdDate) => formatBangkokDateTime(createdDate)
    },
    ...(isAllActivity
      ? [
          {
            title: 'Activity',
            dataIndex: 'brandActivityName',
            key: 'brandActivityName',
            render: (brandActivityName, record) =>
              nameWithId(brandActivityName, record.brandActivityId)
          }
        ]
      : []),
    {
      title: 'User',
      dataIndex: 'userName',
      key: 'userName',
      render: (userName, record) => nameWithId(userName, record.userId)
    },
    {
      title: 'Hero',
      dataIndex: 'heroName',
      key: 'heroName',
      render: (heroName, record) => nameWithId(heroName, record.heroId)
    },
    {
      title: 'วัสดุ',
      dataIndex: 'materialName',
      key: 'materialName',
      render: (materialName) => materialName || '-'
    },
    {
      title: 'จำนวน',
      dataIndex: 'weight',
      key: 'weight',
      render: (weight, record) => formatAmount(record)
    },
    {
      title: 'รูปภาพหลักฐาน',
      dataIndex: 'weighingImage',
      key: 'weighingImage',
      align: 'center',
      render: (weighingImage) => <EvidenceImage images={[weighingImage]} />
    },
    {
      title: 'Location',
      dataIndex: 'locationName',
      key: 'locationName',
      render: (locationName) => locationName || '-'
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag>{statusLabel(status)}</Tag>
    },
    {
      title: 'หมายเหตุ',
      dataIndex: 'brandHeroNote',
      key: 'brandHeroNote',
      width: 200,
      ellipsis: true,
      render: (brandHeroNote) => brandHeroNote || '-'
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <span>
          <Button
            type="primary"
            size="small"
            shape="circle"
            icon="edit"
            onClick={() => onClickForm(record)}
          />
        </span>
      )
    }
  ]

  return (
    <div>
      <Form onSubmit={onSubmit} layout="inline">
        <Row type="flex" gutter={8} align="middle">
          <Col>
            <Form.Item style={{ margin: 0 }} label="วันที่">
              {getFieldDecorator('dateRange', {
                initialValue: todayRange(),
                rules: [
                  {
                    validator: (rule, value, callback) => {
                      if (value && value[0] && value[1]) {
                        if (value[1].isAfter(moment(), 'day')) {
                          callback('ห้ามเลือกวันในอนาคต')
                          return
                        }
                        if (isRangeTooLong(value[0], value[1])) {
                          callback(
                            `เลือกช่วงวันที่ได้ไม่เกิน ${MAX_RANGE_MONTHS} เดือน`
                          )
                          return
                        }
                      }
                      callback()
                    }
                  }
                ]
              })(
                <RangePicker
                  format="DD/MM/YYYY"
                  style={{ width: 240 }}
                  disabledDate={disabledDate}
                  onCalendarChange={onCalendarChange}
                  onOpenChange={onDateRangeOpenChange}
                />
              )}
            </Form.Item>
          </Col>
          <Col>
            <Form.Item style={{ margin: 0 }} label="สถานะ">
              {getFieldDecorator('status')(
                <Select
                  mode="multiple"
                  style={{ width: 220 }}
                  placeholder="ทั้งหมด"
                >
                  {TRANSACTION_STATUS.map((status) => (
                    <Select.Option key={status.value} value={status.value}>
                      {status.label}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col>
            <Form.Item style={{ margin: 0 }} label="User">
              {getFieldDecorator('userId')(
                <Select
                  allowClear
                  showSearch
                  filterOption={false}
                  onSearch={searchUsers}
                  style={{ width: 180 }}
                  placeholder="ทั้งหมด"
                >
                  {userOptions.map((user) => (
                    <Select.Option key={user.id} value={user.id}>
                      {user.name || `#${user.id}`}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col>
            <Form.Item style={{ margin: 0 }} label="Hero">
              {getFieldDecorator('heroId')(
                <Select
                  allowClear
                  showSearch
                  filterOption={false}
                  onSearch={searchHeroes}
                  style={{ width: 180 }}
                  placeholder="ทั้งหมด"
                >
                  {heroOptions.map((hero) => (
                    <Select.Option key={hero.id} value={hero.id}>
                      {hero.name || `#${hero.id}`}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col>
            <Form.Item style={{ margin: 0 }} label="วัสดุ">
              {getFieldDecorator('materialId')(
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  style={{ width: 180 }}
                  placeholder="ทั้งหมด"
                >
                  {materials.map((material) => (
                    <Select.Option key={material.id} value={material.id}>
                      {material.name}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
          {isAllActivity && (
            <Col>
              <Form.Item style={{ margin: 0 }} label="Brand">
                {getFieldDecorator('brandId')(
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    onChange={onBrandChange}
                    style={{ width: 180 }}
                    placeholder="เลือก Brand"
                  >
                    {brandOptions.map((brand) => (
                      <Select.Option key={brand.id} value={brand.id}>
                        {brand.name || `#${brand.id}`}
                      </Select.Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Col>
          )}
          {isAllActivity && (
            <Col>
              <Form.Item style={{ margin: 0 }} label="Activity">
                {getFieldDecorator('brandActivityId')(
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    disabled={activityOptions.length === 0}
                    style={{ width: 220 }}
                    placeholder="เลือก Brand ก่อน"
                  >
                    {activityOptions.map((activity) => (
                      <Select.Option key={activity.id} value={activity.id}>
                        {activity.activityName || `#${activity.id}`}
                      </Select.Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Col>
          )}
          <Col>
            <Button type="primary" htmlType="submit">
              ค้นหา
            </Button>
          </Col>
          <Col>
            <Button type="danger" onClick={() => onClear()}>
              เคลียร์
            </Button>
          </Col>
        </Row>
      </Form>
      <Table
        style={{ marginTop: 16 }}
        bordered
        loading={isLoading}
        scroll={{ x: isAllActivity ? 1600 : 1400 }}
        rowKey={(record) => record.id}
        dataSource={transactions.content || []}
        columns={columns}
        pagination={{
          current: (transactions.number || 0) + 1,
          pageSize: transactions.size || filterOption.size,
          pageSizeOptions: ['10', '20', '50', '100', '200'],
          showSizeChanger: true,
          onShowSizeChange: onShowSizeChange,
          onChange: onChange,
          total: transactions.totalElements || 0
        }}
      />
      <TransactionForm
        visible={visibleForm}
        formState={formState}
        loading={isSaving}
        onOk={onOk}
        onCancel={onCloseForm}
      />
    </div>
  )
}

TransactionTab.propTypes = {
  brandActivityId: PropTypes.number,
  materials: PropTypes.array.isRequired
}

const WrappedTransactionTab = Form.create({
  name: 'BrandActivityTransactionFilter'
})(TransactionTab)
export default WrappedTransactionTab
